import { Box, Button, Typography } from '@mui/material'
import AppRegistrationOutlined from '@mui/icons-material/AppRegistrationOutlined'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth0 } from '@auth0/auth0-react'
import { updateUserSessionsAudit } from '../../services/userServices'
import { USER_SESSION_AUDIT } from '../../constants/constants'

const GETTING_STARTED_KEYS = [
    'registryToolOnlySignIn',
    'registryToolOnlyManageDevices',
]

/**
 * UBQ-5579: the content area for a user whose only role is Vendor Manufacturer, under the usual top bar. Their login
 * is valid (the Registry Tool shares this Auth0 application), so this reads as a normal outcome, not an error.
 */
export default function RegistryToolOnlyPage(): ReactElement {
    const { t } = useTranslation()
    const { logout } = useAuth0()

    return (
        <Box
            id="registry-tool-only"
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box sx={{ maxWidth: 640, width: '100%' }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            bgcolor: 'notice.info.background',
                            color: 'primary.main',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <AppRegistrationOutlined sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h4" gutterBottom>
                        {t('registryToolOnlyTitle')}
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.primary"
                        sx={{ lineHeight: 1.6 }}
                    >
                        {t('registryToolOnlyMessage')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'summary.border',
                        bgcolor: 'summary.background',
                        borderRadius: 1,
                        p: 2.5,
                        mb: 3,
                    }}
                >
                    <Typography variant="sectionHeading" sx={{ mb: 1.5 }}>
                        {t('registryToolOnlyGettingStarted')}
                    </Typography>
                    {GETTING_STARTED_KEYS.map((key) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                mt: 1,
                            }}
                        >
                            <CheckCircleOutline
                                sx={{ fontSize: 20, color: 'primary.main' }}
                            />
                            <Typography variant="body1">{t(key)}</Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        id="registry-tool-only-logout"
                        variant="contained"
                        onClick={async () => {
                            /* Same as the top bar's Log Out: forget the selected org, so the next sign-in in
                               this browser does not start from this user's org. Logout is audited for every
                               user, this one included. */
                            localStorage.removeItem('DEFAULT_ORG')
                            await updateUserSessionsAudit(
                                USER_SESSION_AUDIT.LOGOUT,
                            )
                            logout({
                                logoutParams: {
                                    returnTo: window.location.origin,
                                },
                            })
                        }}
                    >
                        {t('logout')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
